import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

function isAuthRefreshUrl(url: string): boolean {
  return url.includes('/v1/auth/refresh');
}

function skipRefreshOn401(url: string): boolean {
  return (
    url.includes('/v1/auth/login') ||
    isAuthRefreshUrl(url) ||
    url.includes('/v1/user/profile')
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const token = auth.getToken();

  // Refresh calls should not attach a stale access token.
  const withAuth =
    token && !isAuthRefreshUrl(req.url)
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(withAuth).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (skipRefreshOn401(req.url)) {
        return throwError(() => err);
      }
      return from(auth.refreshAccessToken()).pipe(
        switchMap(() => {
          const newTok = auth.getToken();
          if (!newTok) return throwError(() => err);
          return next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${newTok}` },
            }),
          );
        }),
        catchError((refErr) => throwError(() => refErr)),
      );
    }),
  );
};
